import type { Question, Submission } from './types.ts';

/**
 * A compliance document is one whose question is flagged to appear in
 * Compliance Documents (include_in_compliance_documents, migration 0030) AND
 * whose submission has been approved by a reviewer — i.e. marked compliant.
 * Until approval the document stays in the questionnaire view, not the
 * compliance-documents view. Used by both the vendor Compliance Documents page
 * and the admin Compliance Documents tab (the admin expresses the same rule via
 * an approvedIds set — see companyDetailTabs.ts).
 */
export function isApprovedComplianceDoc(
  question: Pick<Question, 'include_in_compliance_documents'> | undefined,
  submission: Pick<Submission, 'status' | 'reverify_requested_at'> | undefined,
): boolean {
  if (question?.include_in_compliance_documents !== true) return false;
  // Approved compliance docs live in the Compliance Documents tab. A doc in a
  // renewal cycle — previously approved, the vendor has uploaded a newer version
  // now awaiting re-review (reverify_requested_at set, status back to pending) —
  // stays pinned here so it never vanishes from the vendor's compliance view
  // mid-renewal. Cleared only when the renewal is itself approved/N-A.
  return submission?.status === 'approved' || submission?.reverify_requested_at != null;
}

/**
 * Keep only submissions that are approved compliance documents (see
 * isApprovedComplianceDoc). Submissions whose question is unknown are dropped
 * (the flag can't be verified).
 */
export function complianceFilteredSubmissions(
  submissions: Submission[],
  questionById: Map<string, Question>,
): Submission[] {
  return submissions.filter((s) => isApprovedComplianceDoc(questionById.get(s.question_id), s));
}

/**
 * Display name for a document row in the Compliance Documents views (admin
 * tab + vendor page): the admin-configured Compliance Document Label when
 * set, else the question title. Blank/whitespace labels count as unset.
 */
export function complianceDocName(q: Pick<Question, 'label' | 'compliance_doc_label'>): string {
  const custom = q.compliance_doc_label?.trim();
  return custom ? custom : q.label;
}
